import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  check,
  foreignKey,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Service Delivery Engine — Financial Tables
// ---------------------------------------------------------------------------

// S.1: Revenue classification for analytics breakdowns
export const REVENUE_TYPES = [
  "service", "product", "tip", "commission", "retainer",
  "parts", "insurance_reimbursement", "deposit", "membership", "refund", "other",
] as const;
export type RevenueType = (typeof REVENUE_TYPES)[number];
// Credit accounts, credit transactions (append-only ledger), gift cards,
// and promo codes. All financial tables have strict integrity constraints.
// ---------------------------------------------------------------------------

export const SERVICE_MEMBERSHIP_PLAN_STATUSES = [
  "draft",
  "active",
  "archived",
] as const;
export type ServiceMembershipPlanStatus =
  (typeof SERVICE_MEMBERSHIP_PLAN_STATUSES)[number];

export const SERVICE_MEMBERSHIP_BILLING_INTERVALS = [
  "week",
  "month",
  "quarter",
  "year",
] as const;
export type ServiceMembershipBillingInterval =
  (typeof SERVICE_MEMBERSHIP_BILLING_INTERVALS)[number];

export const SERVICE_MEMBERSHIP_STATUSES = [
  "trialing",
  "active",
  "past_due",
  "canceled",
  "expired",
] as const;
export type ServiceMembershipStatus =
  (typeof SERVICE_MEMBERSHIP_STATUSES)[number];

export interface ServiceMembershipIncludedCredit {
  accountType: string;
  amount: number;
}

export interface ServiceMembershipRenewalRules {
  gracePeriodDays?: number;
  renewOnPaymentSuccess?: boolean;
  expiresAfterMissedPayments?: boolean;
}

export interface ServiceMembershipProviderPriceIds {
  [provider: string]: string | undefined;
}

// ---------------------------------------------------------------------------
// Service Membership Plans — recurring offers sold to customers
// ---------------------------------------------------------------------------

export const serviceMembershipPlans = pgTable(
  "service_membership_plans",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    slug: text("slug").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    priceCents: integer("price_cents").notNull(),
    currency: text("currency").notNull().default("EUR"),
    billingInterval: text("billing_interval").notNull(),
    providerPriceIds: jsonb("provider_price_ids")
      .$type<ServiceMembershipProviderPriceIds>()
      .notNull()
      .default({}),
    status: text("status").notNull().default("draft"),
    includedCredits: jsonb("included_credits")
      .$type<ServiceMembershipIncludedCredit[]>()
      .notNull()
      .default([]),
    renewalRules: jsonb("renewal_rules").$type<ServiceMembershipRenewalRules>(),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_svc_membership_plans_tenant").on(table.tenantId),
    index("idx_svc_membership_plans_status").on(table.tenantId, table.status),
    uniqueIndex("uq_svc_membership_plan_tenant_id").on(table.tenantId, table.id),
    uniqueIndex("uq_svc_membership_plan_slug").on(table.tenantId, table.slug),
    check(
      "chk_svc_membership_plan_price_non_negative",
      sql`${table.priceCents} >= 0`,
    ),
    check(
      "chk_svc_membership_plan_status",
      sql`${table.status} IN ('draft', 'active', 'archived')`,
    ),
    check(
      "chk_svc_membership_plan_interval",
      sql`${table.billingInterval} IN ('week', 'month', 'quarter', 'year')`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Service Memberships — customer subscription records
// ---------------------------------------------------------------------------

export const serviceMemberships = pgTable(
  "service_memberships",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    planId: uuid("plan_id").notNull(),
    status: text("status").notNull().default("active"),
    currentPeriodStart: timestamp("current_period_start", tz).notNull(),
    currentPeriodEnd: timestamp("current_period_end", tz).notNull(),
    cancelAtPeriodEnd: boolean("cancel_at_period_end").notNull().default(false),
    providerCustomerId: text("provider_customer_id"),
    providerSubscriptionId: text("provider_subscription_id"),
    startedAt: timestamp("started_at", tz).notNull().defaultNow(),
    canceledAt: timestamp("canceled_at", tz),
    expiredAt: timestamp("expired_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_svc_memberships_tenant").on(table.tenantId),
    index("idx_svc_memberships_person").on(
      table.tenantId,
      table.personId,
      table.status,
    ),
    index("idx_svc_memberships_plan").on(
      table.tenantId,
      table.planId,
      table.status,
    ),
    uniqueIndex("uq_svc_membership_provider_subscription").on(
      table.tenantId,
      table.providerSubscriptionId,
    ),
    foreignKey({
      name: "fk_svc_memberships_tenant_plan",
      columns: [table.tenantId, table.planId],
      foreignColumns: [serviceMembershipPlans.tenantId, serviceMembershipPlans.id],
    }).onDelete("restrict").onUpdate("cascade"),
    check(
      "chk_svc_membership_status",
      sql`${table.status} IN ('trialing', 'active', 'past_due', 'canceled', 'expired')`,
    ),
    check(
      "chk_svc_membership_period_order",
      sql`${table.currentPeriodEnd} >= ${table.currentPeriodStart}`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Credit Accounts — prepaid balance per customer per account type
// ---------------------------------------------------------------------------

export const creditPackages = pgTable(
  "credit_packages",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    accountType: text("account_type").notNull().default("session_credits"),
    creditAmount: numeric("credit_amount", { precision: 10, scale: 2 }).notNull(),
    priceCents: integer("price_cents").notNull(),
    currency: text("currency").notNull().default("EUR"),
    expiresInDays: integer("expires_in_days"),
    isActive: boolean("is_active").notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_credit_packages_tenant").on(table.tenantId),
    index("idx_credit_packages_active").on(table.tenantId, table.isActive),
    check("chk_credit_package_amount_positive", sql`${table.creditAmount} > 0`),
    check("chk_credit_package_price_non_negative", sql`${table.priceCents} >= 0`),
    check(
      "chk_credit_package_expiry_non_negative",
      sql`${table.expiresInDays} IS NULL OR ${table.expiresInDays} >= 0`,
    ),
  ],
);

export const CREDIT_ACCOUNT_TYPES = [
  "lesson_hours",
  "session_credits",
  "retainer_hours",
  "general",
] as const;
export type CreditAccountType = (typeof CREDIT_ACCOUNT_TYPES)[number];

export const creditAccounts = pgTable(
  "credit_accounts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(),
    accountType: text("account_type").notNull(), // CreditAccountType
    balance: numeric("balance", { precision: 10, scale: 2 })
      .notNull()
      .default("0"),
    currency: text("currency").notNull().default("EUR"),
    expiresAt: timestamp("expires_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_credit_acct_tenant").on(table.tenantId),
    uniqueIndex("uq_credit_acct_person_type").on(
      table.tenantId,
      table.personId,
      table.accountType,
    ),
    // Financial integrity: balance can never go negative
    check("chk_credit_balance_non_negative", sql`${table.balance} >= 0`),
  ],
);

// ---------------------------------------------------------------------------
// Credit Transactions — APPEND-ONLY financial ledger
// ---------------------------------------------------------------------------
// IMMUTABILITY: This table MUST be append-only at the service layer.
// No UPDATE, no DELETE. Each entry is a financial audit record.
// balanceBefore/balanceAfter enable reconciliation without replay.
//
// Atomic debit pattern:
//   UPDATE credit_accounts SET balance = balance - $cost
//   WHERE tenant_id = $tid AND person_id = $pid AND account_type = $type
//   AND balance >= $cost RETURNING *
// Single statement — no TOCTOU race condition.

export const CREDIT_TRANSACTION_TYPES = [
  "credit",
  "debit",
  "adjustment",
  "refund",
  "expiry",
] as const;
export type CreditTransactionType = (typeof CREDIT_TRANSACTION_TYPES)[number];

export const creditTransactions = pgTable(
  "credit_transactions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    accountId: uuid("account_id").notNull(), // FK to credit_accounts
    transactionType: text("transaction_type").notNull(), // CreditTransactionType
    amount: numeric("amount", { precision: 10, scale: 2 }).notNull(),
    balanceBefore: numeric("balance_before", { precision: 10, scale: 2 }).notNull(),
    balanceAfter: numeric("balance_after", { precision: 10, scale: 2 }).notNull(),
    referenceType: text("reference_type"), // booking, payment, gift_card, promo, manual
    referenceId: uuid("reference_id"),
    description: text("description"),
    // S.1: Revenue classification for PI.3 revenue intelligence MV
    revenueType: text("revenue_type"), // REVENUE_TYPES — nullable for unclassified
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    // No updatedAt — append-only
  },
  (table) => [
    index("idx_credit_txn_tenant").on(table.tenantId),
    index("idx_credit_txn_account").on(table.tenantId, table.accountId),
    index("idx_credit_txn_created").on(table.tenantId, table.createdAt),
    uniqueIndex("uq_credit_txn_purchase_reference")
      .on(table.tenantId, table.referenceId)
      .where(
        sql`${table.transactionType} = 'credit' AND ${table.referenceType} = 'purchase' AND ${table.referenceId} IS NOT NULL`,
      ),
  ],
);

// ---------------------------------------------------------------------------
// Gift Cards — stored-value cards (purchasable by one person, redeemable by another)
// ---------------------------------------------------------------------------
// PII: recipientEmail and recipientName are encrypted via KEK/DEK.
// recipientEmailHash = HMAC for lookup without decryption.

export const GIFT_CARD_STATUSES = [
  "active",
  "depleted",
  "expired",
  "cancelled",
] as const;
export type GiftCardStatus = (typeof GIFT_CARD_STATUSES)[number];

export interface GiftCardMetadata {
  message?: string;
  occasion?: string;
  [key: string]: unknown;
}

export const giftCards = pgTable(
  "gift_cards",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    code: text("code").notNull(),
    initialBalanceCents: integer("initial_balance_cents").notNull(),
    currentBalanceCents: integer("current_balance_cents").notNull(),
    currency: text("currency").notNull().default("EUR"),
    purchasedBy: uuid("purchased_by"), // personId of buyer
    // PII encrypted via KEK/DEK — NEVER log values
    recipientEmailEncrypted: text("recipient_email_encrypted"),
    recipientEmailHash: text("recipient_email_hash"), // HMAC for lookup
    recipientNameEncrypted: text("recipient_name_encrypted"),
    encryptionKeyId: text("encryption_key_id"), // KEK version
    expiresAt: timestamp("expires_at", tz),
    status: text("status").notNull().default("active"), // GiftCardStatus
    metadata: jsonb("metadata").$type<GiftCardMetadata>(),
    redeemedAt: timestamp("redeemed_at", tz),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_gift_card_tenant").on(table.tenantId),
    uniqueIndex("uq_gift_card_code").on(table.tenantId, table.code),
    index("idx_gift_card_recipient").on(
      table.tenantId,
      table.recipientEmailHash,
    ),
    // Financial integrity: balance can never go negative
    check(
      "chk_gift_card_balance_non_negative",
      sql`${table.currentBalanceCents} >= 0`,
    ),
  ],
);

// ---------------------------------------------------------------------------
// Promo Codes — discount codes and vouchers
// ---------------------------------------------------------------------------

export const PROMO_TYPES = [
  "percent_off",
  "fixed_off",
  "free_service",
  "credit_bonus",
] as const;
export type PromoType = (typeof PROMO_TYPES)[number];

export const promoCodes = pgTable(
  "promo_codes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    code: text("code").notNull(),
    promoType: text("promo_type").notNull(), // PromoType
    // value meaning depends on type: 10 = 10% off (percent), 2500 = $25.00 off (fixed), null (free_service)
    value: numeric("value", { precision: 10, scale: 2 }),
    applicableServiceIds: uuid("applicable_service_ids").array(), // null = all services
    usageLimit: integer("usage_limit"), // null = unlimited
    usageCount: integer("usage_count").notNull().default(0),
    perCustomerLimit: integer("per_customer_limit").notNull().default(1),
    minimumSpendCents: integer("minimum_spend_cents"),
    validFrom: timestamp("valid_from", tz),
    validTo: timestamp("valid_to", tz),
    isActive: boolean("is_active").notNull().default(true),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_promo_tenant").on(table.tenantId),
    uniqueIndex("uq_promo_code").on(table.tenantId, table.code),
    index("idx_promo_active").on(table.tenantId, table.isActive),
    check(
      "chk_promo_usage_limit",
      sql`${table.usageLimit} IS NULL OR ${table.usageCount} <= ${table.usageLimit}`,
    ),
  ],
);
